import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { toastr } from 'react-redux-toastr';
import Header from '../../../components/Header';
import { Main, MainContainer } from '../../operational/request/resume/RequestResume';
import { BREADCRUMB_INTEGRACAO } from '../../../utils/breadcrumbIntegracao';
import { ButtonForm } from '../../../components/shared/formStyles.styled';
import { listWebhooks, deleteWebhook } from '../../../services/integracao/webhooks';
import { handleSessionExpired } from '../../../utils/loginHelper';
import WebhookFormModal from './WebhookFormModal';
import WebhookEventsModal from './WebhookEventsModal';
import {
  Toolbar,
  TableCard,
  Table,
  StatusPill,
  EventBadgeList,
  EventBadge,
  RowActions,
  GhostButton,
  DangerButton,
  EmptyState,
} from './webhooks.styles';

const isFeatureEnabled = (value) => value === true || value === 1;

const WEBHOOK_EVENT_TYPES = [
  { type: 'report.delivered', label: 'Laudo entregue' },
];

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('pt-BR');
  } catch (e) {
    return value;
  }
};

class WebhooksPage extends Component {
  state = {
    loading: true,
    webhooks: [],
    eligibleUsers: [],
    formOpen: false,
    editing: null,
    eventsOpen: false,
    eventsUsersApiId: null,
  };

  componentDidMount() {
    if (this.hasAccess()) {
      this.fetchWebhooks();
    }
  }

  hasAccess = () => {
    const usuario = this.props.login?.user?.usuario;
    return isFeatureEnabled(usuario?.integracao_webhooks);
  };

  getToken = () => this.props.login?.user?.token?.access_token || null;

  fetchWebhooks = async () => {
    const token = this.getToken();
    if (!token) {
      toastr.error('Erro', 'Token de autenticação não encontrado.');
      this.setState({ loading: false });
      return;
    }

    this.setState({ loading: true });
    try {
      const response = await listWebhooks(token);
      if (response.status === 401) {
        this.setState({ loading: false });
        handleSessionExpired(this.props.dispatch);
        return;
      }

      this.setState({
        webhooks: response.data?.data || [],
        eligibleUsers: response.data?.meta?.eligible_users || [],
        loading: false,
      });
    } catch (error) {
      console.error(error);
      toastr.error('Erro', 'Não foi possível carregar os webhooks.');
      this.setState({ loading: false, webhooks: [], eligibleUsers: [] });
    }
  };

  openCreate = () => {
    this.setState({ formOpen: true, editing: null });
  };

  openEdit = (webhook) => {
    this.setState({ formOpen: true, editing: webhook });
  };

  closeForm = () => {
    this.setState({ formOpen: false, editing: null });
  };

  openEvents = (webhook = null) => {
    this.setState({
      eventsOpen: true,
      eventsUsersApiId: webhook?.id || null,
    });
  };

  closeEvents = () => {
    this.setState({ eventsOpen: false, eventsUsersApiId: null });
  };

  handleDelete = async (webhook) => {
    const confirmed = window.confirm(
      `Remover o webhook do usuário #${webhook.user_id}?\n${webhook.endpoint}`
    );
    if (!confirmed) return;

    const token = this.getToken();
    if (!token) return;

    try {
      const response = await deleteWebhook(token, webhook.id);
      if (response.status === 401) {
        handleSessionExpired(this.props.dispatch);
        return;
      }
      if (response.status >= 200 && response.status < 300) {
        toastr.success('Sucesso', 'Webhook removido.');
        this.fetchWebhooks();
        return;
      }
      toastr.error('Erro', 'Não foi possível remover o webhook.');
    } catch (error) {
      console.error(error);
      toastr.error('Erro', 'Falha ao remover webhook.');
    }
  };

  userLabel = (userId) => {
    const user = (this.state.eligibleUsers || []).find((u) => Number(u.id) === Number(userId));
    if (!user) return `#${userId}`;
    return `${user.nome || user.email || 'Usuário'} (#${userId})`;
  };

  renderTable = () => {
    const { loading, webhooks } = this.state;

    if (loading) {
      return <EmptyState>Carregando webhooks...</EmptyState>;
    }

    if (!webhooks.length) {
      return (
        <EmptyState>
          Nenhum webhook cadastrado. Clique em &quot;Novo WebHook&quot; para configurar a URL de
          destino.
        </EmptyState>
      );
    }

    return (
      <Table>
        <thead>
          <tr>
            <th>Usuário</th>
            <th>Endpoint</th>
            <th>Eventos</th>
            <th>Status</th>
            <th>Token</th>
            <th>Atualizado</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {webhooks.map((webhook) => (
            <tr key={webhook.id}>
              <td>{this.userLabel(webhook.user_id)}</td>
              <td style={{ maxWidth: 360, wordBreak: 'break-all' }}>{webhook.endpoint}</td>
              <td>
                <EventBadgeList>
                  {WEBHOOK_EVENT_TYPES.map((event) => (
                    <EventBadge key={event.type} title={event.type}>
                      {event.label}
                    </EventBadge>
                  ))}
                </EventBadgeList>
              </td>
              <td>
                <StatusPill $ok={webhook.status === 'ativo'}>{webhook.status || 'ativo'}</StatusPill>
              </td>
              <td>{webhook.has_token_password ? 'Com senha' : '—'}</td>
              <td>{formatDate(webhook.updated_at)}</td>
              <td>
                <RowActions>
                  <GhostButton type="button" onClick={() => this.openEvents(webhook)}>
                    Eventos
                  </GhostButton>
                  <GhostButton type="button" onClick={() => this.openEdit(webhook)}>
                    Editar
                  </GhostButton>
                  <DangerButton type="button" onClick={() => this.handleDelete(webhook)}>
                    Remover
                  </DangerButton>
                </RowActions>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  render() {
    if (!this.hasAccess()) {
      return <Redirect to="/intranet/dashboard" />;
    }

    const { formOpen, editing, eventsOpen, eventsUsersApiId, eligibleUsers } = this.state;
    const canCreate = (eligibleUsers || []).some((u) => !u.has_webhook);

    return (
      <Main>
        <Header
          title="WebHooks"
          breadcrumb={BREADCRUMB_INTEGRACAO}
          actualLink="WebHooks"
        />
        <MainContainer>
          <Toolbar>
            <GhostButton type="button" onClick={() => this.openEvents(null)}>
              Todos os eventos
            </GhostButton>
            <GhostButton type="button" onClick={this.fetchWebhooks}>
              Atualizar
            </GhostButton>
            <ButtonForm type="button" onClick={this.openCreate} disabled={!canCreate}>
              Novo WebHook
            </ButtonForm>
          </Toolbar>

          <TableCard>{this.renderTable()}</TableCard>

          <WebhookFormModal
            isOpen={formOpen}
            onClose={this.closeForm}
            onSaved={this.fetchWebhooks}
            webhook={editing}
            eligibleUsers={eligibleUsers}
            login={this.props.login}
          />

          <WebhookEventsModal
            isOpen={eventsOpen}
            onClose={this.closeEvents}
            usersApiId={eventsUsersApiId}
            login={this.props.login}
          />
        </MainContainer>
      </Main>
    );
  }
}

const mapStateToProps = ({ login }) => ({ login });

export default connect(mapStateToProps)(WebhooksPage);
